import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Settings, FileText, Video, Link as LinkIcon, Save } from 'lucide-react';
import axios from 'axios';

const GestionEstudiantesNuevos = () => {
  const { user, API } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Estado para formulario pendiente
  const [formularioConfig, setFormularioConfig] = useState(null);
  const [formularioForm, setFormularioForm] = useState({
    link_formulario: '',
    titulo: 'Formulario de Información de Estudiante',
    descripcion: 'Por favor completa este formulario antes de acceder al sistema',
    carta_pdf_url: '',
    videos_youtube: []
  });
  const [nuevoVideo, setNuevoVideo] = useState('');
  
  // Estado para registro adicional
  const [registroConfig, setRegistroConfig] = useState(null);
  const [registroForm, setRegistroForm] = useState({
    link_registro: '',
    tipo_formulario: 'wix',
    titulo: 'Formulario de Registro Adicional',
    descripcion: 'Completa este formulario adicional como parte de tu proceso de registro'
  });

  useEffect(() => {
    loadAllConfigs();
  }, []);

  const loadAllConfigs = async () => {
    setLoading(true);
    await Promise.all([
      loadFormularioConfig(),
      loadRegistroConfig()
    ]);
    setLoading(false);
  };

  const loadFormularioConfig = async () => {
    try {
      const response = await axios.get(`${API}/formulario-config`);
      if (response.data) {
        setFormularioConfig(response.data);
        setFormularioForm({
          link_formulario: response.data.link_formulario || '',
          titulo: response.data.titulo || 'Formulario de Información de Estudiante',
          descripcion: response.data.descripcion || 'Por favor completa este formulario antes de acceder al sistema',
          carta_pdf_url: response.data.carta_pdf_url || '',
          videos_youtube: response.data.videos_youtube || []
        });
      }
    } catch (error) {
      console.log('No formulario config found');
    }
  };

  const loadRegistroConfig = async () => {
    try {
      const response = await axios.get(`${API}/registro-config`);
      if (response.data) {
        setRegistroConfig(response.data);
        setRegistroForm({
          link_registro: response.data.link_registro || '',
          tipo_formulario: response.data.tipo_formulario || 'wix',
          titulo: response.data.titulo || 'Formulario de Registro Adicional',
          descripcion: response.data.descripcion || 'Completa este formulario adicional como parte de tu proceso de registro'
        });
      }
    } catch (error) {
      console.log('No registro config found');
    }
  };

  const handleSaveFormulario = async () => {
    if (!formularioForm.link_formulario.trim()) {
      toast.error('El link del formulario es requerido');
      return;
    }

    setSaving(true);
    try {
      const method = formularioConfig ? 'PUT' : 'POST';
      const url = formularioConfig 
        ? `${API}/admin/formulario-config/${formularioConfig.id}`
        : `${API}/admin/formulario-config`;

      await axios({
        method,
        url,
        data: formularioForm,
        headers: { 'Content-Type': 'application/json' }
      });

      toast.success('Configuración guardada exitosamente');
      loadFormularioConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRegistro = async () => {
    if (!registroForm.link_registro.trim()) {
      toast.error('El link de registro es requerido');
      return;
    }

    setSaving(true);
    try {
      const method = registroConfig ? 'PUT' : 'POST';
      const url = registroConfig 
        ? `${API}/admin/registro-config/${registroConfig.id}`
        : `${API}/admin/registro-config`;

      await axios({
        method,
        url,
        data: registroForm
      });

      toast.success('Configuración de registro guardada exitosamente');
      loadRegistroConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const agregarVideo = () => {
    if (!nuevoVideo.trim()) {
      toast.error('Por favor ingrese una URL de YouTube');
      return;
    }

    if (!nuevoVideo.includes('youtube.com') && !nuevoVideo.includes('youtu.be')) {
      toast.error('Por favor ingrese una URL válida de YouTube');
      return;
    }

    setFormularioForm(prev => ({
      ...prev,
      videos_youtube: [...prev.videos_youtube, nuevoVideo.trim()]
    }));
    setNuevoVideo('');
    toast.success('Video agregado');
  };

  const eliminarVideo = (index) => {
    setFormularioForm(prev => ({
      ...prev,
      videos_youtube: prev.videos_youtube.filter((_, i) => i !== index)
    }));
    toast.success('Video eliminado');
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="p-4">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-semibold text-red-600 mb-2">Acceso Denegado</h3>
            <p className="text-gray-600">Solo administradores pueden acceder a esta sección.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">Gestión de Estudiantes Nuevos</h2>
        </div>
        <p className="text-gray-600">
          Configura formularios, videos instructivos y links de registro para estudiantes nuevos.
        </p>
      </div>

      <Tabs defaultValue="formulario" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="formulario" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Formulario & Recursos
          </TabsTrigger>
          <TabsTrigger value="registro" className="flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            Registro Adicional
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: FORMULARIO PENDIENTE, VIDEOS Y CARTA PDF */}
        <TabsContent value="formulario" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Formulario de Información & Recursos</CardTitle>
              <p className="text-sm text-gray-600">
                Los estudiantes verán esto cuando se registren y estén pendientes de aprobación.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="link_formulario">Link del Formulario (Google Forms) *</Label>
                <Input
                  id="link_formulario"
                  type="url"
                  placeholder="https://forms.google.com/..."
                  value={formularioForm.link_formulario}
                  onChange={(e) => setFormularioForm({...formularioForm, link_formulario: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="titulo">Título</Label>
                  <Input
                    id="titulo"
                    value={formularioForm.titulo}
                    onChange={(e) => setFormularioForm({...formularioForm, titulo: e.target.value})}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="carta_pdf_url">URL de Carta PDF (Opcional)</Label>
                  <Input
                    id="carta_pdf_url"
                    type="url"
                    placeholder="https://ejemplo.com/carta.pdf"
                    value={formularioForm.carta_pdf_url}
                    onChange={(e) => setFormularioForm({...formularioForm, carta_pdf_url: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="descripcion">Descripción</Label>
                <Textarea
                  id="descripcion"
                  value={formularioForm.descripcion}
                  onChange={(e) => setFormularioForm({...formularioForm, descripcion: e.target.value})}
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div>
                <Label>Videos de YouTube (Instrucciones)</Label>
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      type="url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={nuevoVideo}
                      onChange={(e) => setNuevoVideo(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), agregarVideo())}
                    />
                    <Button 
                      type="button" 
                      onClick={agregarVideo}
                      variant="outline"
                      className="whitespace-nowrap"
                    >
                      + Agregar
                    </Button>
                  </div>
                  
                  {formularioForm.videos_youtube.length > 0 && (
                    <div className="space-y-2 mt-3">
                      {formularioForm.videos_youtube.map((video, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                          <Video className="h-4 w-4 text-red-600" />
                          <span className="text-sm text-gray-600 flex-1 truncate">{video}</span>
                          <Button
                            type="button"
                            onClick={() => eliminarVideo(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Button 
                onClick={handleSaveFormulario} 
                disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: REGISTRO ADICIONAL (WIX/GOOGLE CLASSROOM) */}
        <TabsContent value="registro" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Link de Registro Adicional</CardTitle>
              <p className="text-sm text-gray-600">
                Formulario adicional que los estudiantes deben completar (Wix o Google Classroom).
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Tipo de Formulario</Label>
                <Select
                  value={registroForm.tipo_formulario}
                  onValueChange={(value) => setRegistroForm({...registroForm, tipo_formulario: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wix">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-globe text-blue-600"></i>
                        <span>Wix Forms</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="google_classroom">
                      <div className="flex items-center gap-2">
                        <i className="fab fa-google text-red-600"></i>
                        <span>Google Classroom</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {registroForm.tipo_formulario === 'wix' ? 'Formulario de Wix' : 'Formulario de Google Classroom'}
                </p>
              </div>

              <div>
                <Label htmlFor="link_registro">Link del Formulario *</Label>
                <Input
                  id="link_registro"
                  type="url"
                  placeholder={registroForm.tipo_formulario === 'wix' ? 'https://ejemplo.wixsite.com/formulario' : 'https://classroom.google.com/...'}
                  value={registroForm.link_registro}
                  onChange={(e) => setRegistroForm({...registroForm, link_registro: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="titulo_registro">Título</Label>
                  <Input
                    id="titulo_registro"
                    value={registroForm.titulo}
                    onChange={(e) => setRegistroForm({...registroForm, titulo: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="descripcion_registro">Descripción</Label>
                <Textarea
                  id="descripcion_registro"
                  value={registroForm.descripcion}
                  onChange={(e) => setRegistroForm({...registroForm, descripcion: e.target.value})}
                  className="mt-1"
                  rows={3}
                />
              </div>

              <Button 
                onClick={handleSaveRegistro} 
                disabled={saving}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GestionEstudiantesNuevos;
